import { useState, useEffect } from "react";
import { Calendar, MapPin, Mail, Phone, Clock, Users, Star, User, ArrowRight, CheckCircle2, AlertCircle, XCircle, Gift, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import Axios from "../../services/AxiosInterceptor";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

const STORAGE_URL = "https://yogamatic.in/api/upload/";

const Events = () => {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [seats, setSeats] = useState(1);
  const [transactionId, setTransactionId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cxReview, setCxReview] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [averageRating, setAverageRating] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const userId = localStorage.getItem("userId");

  const getImageUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http") || path.startsWith("https")) return path;
    return `${STORAGE_URL}${path}`;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  useEffect(() => {
    const fetchRatingData = async () => {
      try {
        const response = await Axios.post("/api/cx_rating", { data: { serviceId: id } });
        if (response.data.success) {
          const ratings = response.data.data;
          const avg = ratings.length > 0 ? ratings.reduce((t, i) => t + i.rating, 0) / ratings.length : 0;
          setAverageRating(avg);
          setCxReview(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching ratings:", error);
      }
    };
    fetchRatingData();
  }, [id]);

  useEffect(() => {
    async function loadEvent() {
      try {
        const res = await Axios.get(`api/events/${id}`);
        setEvent(res.data);
      } catch (err) {
        console.error("Error fetching event:", err);
      } finally {
        setLoading(false);
      }
    }
    loadEvent();
  }, [id]);

  const handleManualPayment = async (ticket) => {
    if (!transactionId) {
      alert("Please enter the Transaction ID / UTR Number.");
      return;
    }
    setSubmitting(true);
    const email = localStorage.getItem("email");
    const mobile = localStorage.getItem("mobile");
    const username = localStorage.getItem("Username");
    const amount = ticket.price * seats;
    try {
      const response = await Axios.post("/api/event-manual-payment", {
        amount, userId, eventId: event._id, venue: event.venue,
        date: new Date(event.date).toISOString().split("T")[0],
        time: event.time, seatsBooked: seats, transactionId, email, mobile, username,
        eventTitle: event.title,
      });
      if (response.data.success) {
        setShowSuccessModal(true);
      } else {
        alert("Payment confirmation failed: " + response.data.message);
      }
    } catch (error) {
      console.error("Payment Error:", error);
      alert("An error occurred while processing payment.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRazorpayPayment = async (ticket) => {
    try {
      const res = await loadRazorpayScript();
      if (!res) {
        alert("Razorpay SDK failed to load. Please check your internet connection.");
        return;
      }
      const amount = ticket.price * seats;
      const email = localStorage.getItem("email");
      const mobile = localStorage.getItem("mobile");
      const orderRes = await Axios.post("/event/create-order", {
        amount, userId, serviceId: event._id, location: event.venue,
        date: new Date(event.date).toISOString().split("T")[0],
        timeSlot: event.time, seatsBooked: seats, email, mobile, resource: "Ajay",
      });
      if (!orderRes.data.success) {
        alert(orderRes.data.details || "Failed to create payment order.");
        return;
      }
      const { order } = orderRes.data;
      const options = {
        key: "rzp_test_Rlc3EB1a50nRq5",
        amount: order.amount, currency: "INR", name: event.title,
        description: "Event Ticket Booking", order_id: order.id,
        handler: async function (response) {
          const confirmRes = await Axios.post("/event/confirm-payment", {
            orderId: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id, razorpay_signature: response.razorpay_signature,
            email, eventId: event._id, seatsBooked: seats, amount, userId, location: event.venue,
            date: new Date(event.date).toISOString().split("T")[0],
            timeSlot: event.time, mobile, resource: "Ajay",
          });
          const booking = confirmRes?.data?.booking;
          navigate(`/payment-details/${booking?._id}`, { state: { booking, event, razorpayResponse: response } });
        },
        prefill: { name: "Customer", email, contact: mobile },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      let errorMessage = "Payment Failed. ";
      if (err.response) errorMessage += err.response.data?.details || err.response.data?.message || "Please try again.";
      else if (err.request) errorMessage += "Network error. Please check your internet connection.";
      else errorMessage += err.message || "Please try again later.";
      alert(errorMessage);
    }
  };

  const handleBookClick = (ticket) => {
    if (!userId) {
      setShowLoginModal(true);
      return;
    }
    handleManualPayment(ticket);
  };

  const fullStars = Math.round(averageRating);
  const sortedReviews = [...cxReview].sort((a, b) => b.rating - a.rating);
  const visibleReviews = showAll ? sortedReviews : sortedReviews.slice(0, 3);

  if (loading) {
    return (
      <main style={{ display: "flex", justifyContent: "center", padding: "100px 0" }}>
        <Loader2 size={32} style={{ animation: "spin 0.8s linear infinite", color: "var(--ym-sage)" }} />
      </main>
    );
  }

  if (!event) {
    return (
      <main className="ym-section" style={{ paddingTop: 60 }}>
        <div className="ym-container" style={{ textAlign: "center", padding: "80px 24px" }}>
          <p className="ym-text" style={{ color: "var(--ym-muted)" }}>Event not found</p>
        </div>
      </main>
    );
  }

  return (
    <main>
      {/* ====== HERO ====== */}
      <section style={{ position: "relative", height: 420, overflow: "hidden" }}>
        <img
          src={getImageUrl(event.heroImage)}
          alt={event.title}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(23,32,27,0.7) 0%, rgba(23,32,27,0.3) 60%, rgba(23,32,27,0.1) 100%)" }} />
        <div style={{ position: "relative", zIndex: 2, height: "100%", display: "flex", alignItems: "flex-end" }}>
          <div className="ym-container" style={{ paddingBottom: 48 }}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: "var(--ym-radius)", background: "rgba(255,255,255,0.12)", color: "#fff", fontSize: 12, fontWeight: 700, backdropFilter: "blur(6px)", marginBottom: 14 }}>
                <Calendar size={14} />
                {new Date(event.date).toDateString()} — {event.time}
              </span>
              <h1 style={{ fontSize: "clamp(1.8rem, 4.5vw, 3rem)", fontWeight: 900, color: "#fff", margin: "0 0 8px", lineHeight: 1.15 }}>{event.title}</h1>
              {event.subtitle && <p style={{ fontSize: 17, color: "rgba(255,255,255,0.85)", margin: "0 0 6px", fontWeight: 600 }}>{event.subtitle}</p>}
              {event.about && <p style={{ fontSize: 14, color: "rgba(255,255,255,0.75)", margin: 0, maxWidth: 560, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{event.about}</p>}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
                {event.tags?.map((tag, i) => (
                  <span key={i} style={{ padding: "4px 12px", borderRadius: "var(--ym-radius)", background: "rgba(255,255,255,0.1)", color: "#fff", fontSize: 12, fontWeight: 600 }}>{tag}</span>
                ))}
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
                <a href="#tickets" className="ym-button ym-button-gold" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px", minHeight: 0 }}>
                  <Gift size={16} /> Get Tickets
                </a>
                {event.schedule?.length > 0 && (
                  <a href="#schedule" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px", borderRadius: "var(--ym-radius)", background: "rgba(255,255,255,0.1)", color: "#fff", fontWeight: 700, fontSize: 13, textDecoration: "none", backdropFilter: "blur(6px)" }}>
                    <Clock size={16} /> See Schedule
                  </a>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====== MAIN CONTENT ====== */}
      <section className="ym-section" style={{ paddingTop: 40 }}>
        <div className="ym-container">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 28, alignItems: "start" }}>
            {/* ---- LEFT COLUMN ---- */}
            <div style={{ display: "grid", gap: 24 }}>

              {/* About */}
              <motion.div className="ym-card ym-card-pad" variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                <h2 className="ym-service-title" style={{ fontSize: 20, marginBottom: 16 }}>About the Event</h2>
                <p className="ym-text" style={{ lineHeight: 1.7 }}>{event.about}</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 20 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <Calendar size={18} style={{ color: "var(--ym-gold)", flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "var(--ym-muted)", textTransform: "uppercase" }}>When</p>
                      <p style={{ margin: "2px 0 0", fontSize: 14, fontWeight: 700 }}>{new Date(event.date).toDateString()} • {event.time}</p>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <MapPin size={18} style={{ color: "var(--ym-gold)", flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "var(--ym-muted)", textTransform: "uppercase" }}>Where</p>
                      <p style={{ margin: "2px 0 0", fontSize: 14, fontWeight: 700 }}>{event.venue}</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Speakers */}
              {event.speakers?.length > 0 && (
                <motion.div className="ym-card ym-card-pad" variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                  <h3 className="ym-service-title" style={{ fontSize: 18, marginBottom: 16 }}>Featured Speakers</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
                    {event.speakers.map((sp, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: 14, borderRadius: "var(--ym-radius)", background: "rgba(38,66,54,0.03)" }}>
                        <div style={{ width: 48, height: 48, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "var(--ym-mint)" }}>
                          {sp.image ? <img src={getImageUrl(sp.image)} alt={sp.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <User size={24} style={{ margin: 12, color: "var(--ym-sage)" }} />}
                        </div>
                        <div>
                          <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{sp.name}</p>
                          <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--ym-muted)" }}>{sp.title}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Schedule */}
              {event.schedule?.length > 0 && (
                <motion.div id="schedule" className="ym-card ym-card-pad" variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                  <h3 className="ym-service-title" style={{ fontSize: 18, marginBottom: 16 }}>Schedule</h3>
                  <div style={{ display: "grid", gap: 4 }}>
                    {event.schedule.map((s, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderRadius: "var(--ym-radius)", background: i % 2 === 0 ? "rgba(38,66,54,0.03)" : "transparent" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ym-gold)", fontFamily: "monospace", minWidth: 60 }}>{s.time}</span>
                          <div>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{s.title}</p>
                            {s.location && <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--ym-muted)" }}>{s.location}</p>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Gallery */}
              {event.gallery?.length > 0 && (
                <motion.div className="ym-card ym-card-pad" variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                  <h3 className="ym-service-title" style={{ fontSize: 18, marginBottom: 16 }}>Gallery</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                    {event.gallery.map((img, i) => (
                      <img key={i} src={getImageUrl(img)} alt="" style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: "var(--ym-radius)" }} />
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* ---- RIGHT SIDEBAR ---- */}
            <div style={{ position: "sticky", top: 96, display: "grid", gap: 20 }}>
              {/* Tickets */}
              <motion.div id="tickets" className="ym-card" style={{ padding: 24 }} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                <h4 className="ym-service-title" style={{ fontSize: 16, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
                  <Gift size={18} style={{ color: "var(--ym-gold)" }} />
                  Reserve Your Spot
                </h4>

                {event.tickets?.map((t, i) => (
                  <div key={i} style={{ marginBottom: i < event.tickets.length - 1 ? 20 : 0, paddingBottom: i < event.tickets.length - 1 ? 20 : 0, borderBottom: i < event.tickets.length - 1 ? "1px solid var(--ym-line)" : "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>{t.tier}</span>
                      <span style={{ fontWeight: 800, fontSize: 18, color: "var(--ym-sage)" }}>₹{t.price}</span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                      <Users size={14} style={{ color: "var(--ym-muted)" }} />
                      {event.availableSeats > 0 ? (
                        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ym-sage)" }}>{event.availableSeats} seats available</span>
                      ) : (
                        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ym-coral)" }}>Sold Out</span>
                      )}
                    </div>

                    {event.availableSeats > 0 && (
                      <>
                        <div style={{ marginBottom: 14 }}>
                          <label className="ym-label" style={{ marginBottom: 6, display: "block" }}>Number of Seats</label>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <button onClick={() => setSeats(Math.max(1, seats - 1))} className="ym-icon-box" style={{ width: 38, height: 38, cursor: "pointer", border: "1px solid var(--ym-line)" }} disabled={seats <= 1}>−</button>
                            <span style={{ width: 48, textAlign: "center", fontWeight: 800, fontSize: 18 }}>{seats}</span>
                            <button onClick={() => setSeats(Math.min(event.availableSeats, seats + 1))} className="ym-icon-box" style={{ width: 38, height: 38, cursor: "pointer", border: "1px solid var(--ym-line)" }} disabled={seats >= event.availableSeats}>+</button>
                          </div>
                          <p style={{ margin: "8px 0 0", fontSize: 13, color: "var(--ym-muted)" }}>Total: <strong style={{ color: "var(--ym-ink)" }}>₹{seats * t.price}</strong></p>
                        </div>

                        {userId && seats > 0 ? (
                          <div style={{ background: "rgba(255,248,237,0.5)", padding: 16, borderRadius: "var(--ym-radius)", border: "1px solid rgba(245,182,66,0.15)" }}>
                            <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>UPI Payment</p>
                            <p style={{ fontSize: 12, color: "var(--ym-muted)", marginBottom: 12 }}>
                              Enter the correct Transaction ID / UTR. Our team verifies payments based on this information.
                            </p>
                            <div style={{ background: "#fff", padding: 12, borderRadius: "var(--ym-radius)", marginBottom: 12, textAlign: "center", border: "1px solid var(--ym-line)" }}>
                              <p style={{ fontWeight: 700, fontSize: 13, margin: "0 0 4px" }}>UPI ID: ajay.nama1995@ybl</p>
                              <div style={{ width: 120, height: 120, margin: "8px auto", background: "#f0f0f0", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <img src="/ajay1.jpeg" alt="UPI QR" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                              </div>
                            </div>
                            <div style={{ marginBottom: 12 }}>
                              <label className="ym-label" style={{ marginBottom: 4, display: "block", fontSize: 12 }}>Transaction ID / UTR</label>
                              <input
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value)}
                                placeholder="e.g. 123456789012"
                                style={{ width: "100%", padding: "10px 12px", borderRadius: "var(--ym-radius)", border: "1px solid var(--ym-line)", fontSize: 13, fontFamily: "inherit", outline: "none" }}
                                onFocus={(e) => e.currentTarget.style.borderColor = "var(--ym-gold)"}
                                onBlur={(e) => e.currentTarget.style.borderColor = "var(--ym-line)"}
                              />
                            </div>
                            <button
                              onClick={() => handleBookClick(t)}
                              disabled={submitting}
                              className="ym-button ym-button-gold"
                              style={{ width: "100%", justifyContent: "center", minHeight: 44 }}
                            >
                              {submitting ? <Loader2 size={16} style={{ animation: "spin 0.8s linear infinite" }} /> : <CheckCircle2 size={16} />}
                              {submitting ? "Processing..." : "Confirm Payment"}
                            </button>
                          </div>
                        ) : (
                          <div style={{ padding: 16, borderRadius: "var(--ym-radius)", background: "rgba(38,66,54,0.03)", border: "1px dashed var(--ym-line)", textAlign: "center" }}>
                            <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: "var(--ym-muted)" }}>Complete Selection to Pay</p>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, alignItems: "center" }}>
                              <span style={{ color: seats > 0 ? "var(--ym-sage)" : "var(--ym-muted)" }}>{seats > 0 ? "✅" : "⭕"} Enter Seats</span>
                              <span style={{ color: userId ? "var(--ym-sage)" : "var(--ym-coral)" }}>{userId ? "✅" : "❌"} Login Required</span>
                            </div>
                            {!userId && (
                              <button
                                onClick={() => { localStorage.setItem("redirectAfterLogin", location.pathname); navigate("/cx_login"); }}
                                className="ym-button ym-button-primary"
                                style={{ marginTop: 12, width: "100%", justifyContent: "center" }}
                              >
                                Sign In Now
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {event.availableSeats === 0 && (
                      <button disabled className="ym-button" style={{ width: "100%", justifyContent: "center", opacity: 0.4 }}>Sold Out</button>
                    )}
                  </div>
                ))}
              </motion.div>

              {/* Cancellation Policy */}
              {event?.policies && (
                <motion.div className="ym-card ym-card-pad" variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                  <h5 style={{ fontWeight: 700, fontSize: 14, margin: "0 0 8px" }}>Cancellation Policy</h5>
                  <p style={{ fontSize: 13, color: "var(--ym-muted)", margin: 0, lineHeight: 1.6 }}>{event.policies}</p>
                </motion.div>
              )}

              {/* Contact */}
              {event?.contact && (
                <motion.div className="ym-card ym-card-pad" variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                  <h5 style={{ fontWeight: 700, fontSize: 14, margin: "0 0 10px" }}>Contact</h5>
                  <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Mail size={14} style={{ color: "var(--ym-muted)" }} /> {event.contact.email}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Phone size={14} style={{ color: "var(--ym-muted)" }} /> {event.contact.phone}</div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ====== REVIEWS ====== */}
      {cxReview.length > 0 && (
        <section className="ym-section" style={{ paddingTop: 0 }}>
          <div className="ym-container" style={{ maxWidth: 900 }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <span className="ym-eyebrow" style={{ marginInline: "auto" }}>
                <Star size={14} />
                Reviews
              </span>
              <h2 className="ym-title-small">See What Our Customers Say</h2>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: 20, lineHeight: 1 }}>{"⭐".repeat(fullStars)}</span>
                <span style={{ fontWeight: 800, fontSize: 15 }}>{averageRating.toFixed(1)}</span>
                <span style={{ fontSize: 13, color: "var(--ym-muted)" }}>({cxReview.length} reviews)</span>
              </div>
            </div>

            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} style={{ display: "grid", gap: 14 }}>
              {visibleReviews.map((item, index) => {
                const d = new Date(item.date);
                return (
                  <motion.div key={item._id || index} variants={fadeUp} className="ym-review-card" style={{ padding: "16px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg, var(--ym-sage), #1b3027)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14, fontWeight: 800, flexShrink: 0 }}>
                          {(item.username || "U")[0].toUpperCase()}
                        </div>
                        <div>
                          <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{item.username || "Unknown User"}</p>
                          <p style={{ margin: 0, fontSize: 12, color: "var(--ym-muted)" }}>{d.toLocaleDateString()} • {d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                        </div>
                      </div>
                      <span style={{ fontSize: 15, color: "#ffc107" }}>{"⭐".repeat(item.rating)}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: "var(--ym-ink)", fontStyle: "italic" }}>“{item.message}”</p>
                  </motion.div>
                );
              })}
            </motion.div>

            {sortedReviews.length > 3 && (
              <div style={{ textAlign: "center", marginTop: 24 }}>
                <button onClick={() => setShowAll(!showAll)} className="ym-button ym-button-gold" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {showAll ? "Show Less" : "See More Reviews"}
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ====== LOGIN MODAL ====== */}
      {showLoginModal && (
        <div className="myb-overlay" onClick={() => setShowLoginModal(false)}>
          <div className="myb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ym-card" style={{ padding: 0, maxWidth: 400, width: "90vw" }}>
              <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--ym-line)" }}>
                <h4 className="ym-service-title" style={{ margin: 0, fontSize: 16 }}>Sign In Required</h4>
              </div>
              <div style={{ padding: "20px 24px" }}>
                <p style={{ margin: "0 0 20px", color: "var(--ym-muted)", lineHeight: 1.5, fontSize: 14 }}>
                  To book this event, please sign in to your account.
                </p>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={() => setShowLoginModal(false)} className="ym-button ym-button-ghost">Close</button>
                  <button onClick={() => { localStorage.setItem("redirectAfterLogin", location.pathname); navigate("/cx_login"); }} className="ym-button ym-button-gold">Sign In</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====== SUCCESS MODAL ====== */}
      {showSuccessModal && (
        <div className="myb-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="myb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ym-card" style={{ padding: 0, maxWidth: 440, width: "90vw" }}>
              <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--ym-line)" }}>
                <h4 className="ym-service-title" style={{ margin: 0, fontSize: 16, color: "var(--ym-sage)" }}>Payment Submitted</h4>
              </div>
              <div style={{ padding: "24px 24px", textAlign: "center" }}>
                <CheckCircle2 size={48} style={{ color: "var(--ym-sage)", marginBottom: 12 }} />
                <h4 style={{ fontWeight: 800, fontSize: 16, margin: "0 0 8px" }}>Booking Pending Verification</h4>
                <p style={{ fontSize: 13, color: "var(--ym-muted)", lineHeight: 1.6, margin: "0 0 20px" }}>
                  Thank you! We have received your payment details. Our team will verify the transaction and confirm your booking shortly.
                </p>
                <button
                  onClick={() => { setShowSuccessModal(false); navigate("/My_Bookings"); }}
                  className="ym-button ym-button-gold"
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  Go to My Bookings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Events;
